import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setActiveAccountUuid, getActiveAccountUuid } from '@/services/api'
import { useDomainStore } from '@/stores/domains'
import { useBillingStore } from '@/stores/billing'
import type { User } from '@/types'

export interface WorkspaceAccount {
  uuid: string
  name: string
  type: 'personal' | 'business'
  company_name: string | null
  role: 'owner' | 'admin' | 'member'
  is_primary: boolean
  vat_number: string | null
  vat_verified: boolean
  vat_verified_at: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  expiration_warning_lead_days?: number | null
  subscription: {
    tier: string | null
    tier_name: string | null
    status: string
    domain_limit: number
    domains_used: number
  } | null
}

export const useAccountContextStore = defineStore('accountContext', () => {
  const accounts = ref<WorkspaceAccount[]>([])
  const activeAccountUuid = ref<string | null>(getActiveAccountUuid())
  const initialized = ref(false)

  const activeAccount = computed(() =>
    accounts.value.find((a) => a.uuid === activeAccountUuid.value) ?? null,
  )

  const hasMultipleAccounts = computed(() => accounts.value.length >= 2)

  function hydrateFromUser(user: User): void {
    accounts.value = user.accounts ?? []
    const fallback =
      user.primary_account_uuid ??
      user.personal_account_uuid ??
      accounts.value[0]?.uuid ??
      null

    if (
      !activeAccountUuid.value ||
      !accounts.value.some((a) => a.uuid === activeAccountUuid.value)
    ) {
      setActiveAccount(fallback)
    } else {
      setActiveAccountUuid(activeAccountUuid.value)
    }
    initialized.value = true
  }

  function setActiveAccount(uuid: string | null): void {
    activeAccountUuid.value = uuid
    setActiveAccountUuid(uuid)
  }

  async function switchAccount(uuid: string): Promise<void> {
    if (!accounts.value.some((a) => a.uuid === uuid)) {
      return
    }
    setActiveAccount(uuid)

    const domainsStore = useDomainStore()
    const billingStore = useBillingStore()
    await Promise.all([
      domainsStore.fetchDomains(),
      domainsStore.fetchDashboardSummary(),
      domainsStore.fetchDashboardHealth(),
      billingStore.fetchSubscription(),
    ])
  }

  function clear(): void {
    accounts.value = []
    setActiveAccount(null)
    initialized.value = false
  }

  function patchAccount(uuid: string, patch: Partial<WorkspaceAccount>): void {
    const index = accounts.value.findIndex((a) => a.uuid === uuid)
    if (index >= 0) {
      accounts.value[index] = { ...accounts.value[index], ...patch }
    }
  }

  return {
    accounts,
    activeAccountUuid,
    activeAccount,
    hasMultipleAccounts,
    initialized,
    hydrateFromUser,
    setActiveAccount,
    switchAccount,
    clear,
    patchAccount,
  }
})
